// ════════════════════════════════════════════════════════════════════════════
// PRODUCT MODAL
// ════════════════════════════════════════════════════════════════════════════
//
// When a product card is clicked on the index, instead of navigating to
// /products/HANDLE we:
//   1. fetch the product page HTML
//   2. parse out the product UI (.product-page-wrapper + image-expand modal)
//   3. inject it into a full-viewport black overlay
//   4. load style-product.css temporarily so the product layout styles apply
//   5. re-execute the product page scripts so variants / expand / instruction-
//      manual / image zoom all work
//   6. update the URL via pushState — browser doesn't navigate, just displays
//      /products/HANDLE in the address bar; refresh = real page, back = close
//
// The modal uses `transform: translateZ(0)` so it acts as the containing block
// for any `position: fixed` descendants (style-product.css uses fixed on
// .product-page-scrollable and .product-description-container). Without that,
// those elements would anchor to the raw viewport and paint behind the modal.

const PRODUCT_MODAL = {
  overlayId: "claustrum-product-modal",
  styleId: "claustrum-product-modal-style",
  cssId: "claustrum-product-modal-css",
  readyEvent: "ClaustrumProductModalReady",
  cache: Object.create(null),
  closeTimer: null,        // pending close-finalize timeout, so a quick re-open can cancel it
  fadeInTimer: null,       // pending fadeIn cleanup timeout from a previous close
  fetchTimeoutMs: 8000,
  _footerNode: null,       // the footer node detached while the PDP is open (see pmRemoveFooter)
  _footerSlot: null,       // { parent, next } slot to restore the footer into on close
  _openSeq: 0,             // monotonic counter → unique ready-event name per open (see pmRunScripts)
  _htmlOverflowPrev: "",   // <html> overflow captured before the scroll-lock, restored on close
  _bodyOverflowPrev: "",   // <body> overflow captured before the lock (desktop path), restored on close
  _scrollLockY: 0,         // underlay scrollY captured at open; body is pinned at top:-Y, restored on close
  loaderId: "claustrum-product-modal-loader",
  loaderTimer: null,       // delayed-show timer for the cache-miss loading animation
};

// Fetch target toggle. Shopify serves the storefront product page from its
// internal full-page cache (Response header `ETag: page_cache:…ProductDetails
// Controller`, ~100ms processing) — but ONLY on the PUBLISHED theme, and only
// when the request matches the cached entry's key. The cache Varies on
// `Accept`, so a bare fetch (`Accept: */*`) misses; pmFetchOk now sends a
// navigation-style `Accept: text/html` to match.
//
//   true  → canonical /products/x. The bet for PRODUCTION: should land the
//           ~100ms page-cache hit a direct click-through gets. UNVERIFIED for
//           a fetch (cors) vs a document nav until tested on a published theme.
//   false → ?view=modal slim fragment (layout none, product-body only). Always
//           cache-cold (distinct URL), but skips theme.liquid so it renders far
//           less Liquid.
//
// Currently FALSE because development happens on a PREVIEW theme, which Shopify
// never page-caches — so canonical's only upside doesn't exist here, and on the
// preview theme canonical renders the full heavy page (~1047ms, complexity
// ~1042) vs ?view=modal (~295ms, complexity ~281). Flip to true and verify the
// page_cache ETag at publish time. See memory: pdp-modal-page-cache.
var PM_FETCH_CANONICAL = false;

function pmModalUrl(url) {
  if (PM_FETCH_CANONICAL) return url;
  return url + (url.indexOf("?") === -1 ? "?" : "&") + "view=modal";
}

// Fetch with timeout that rejects on non-OK status. Storefront 403s
// (Shopify bot/rate-limit) used to be silently cached as "valid" HTML,
// poisoning every subsequent click on that product.
// `Accept: text/html` mimics a document navigation so the request matches
// Shopify's `Vary: Accept` page-cache key (a bare fetch sends `*/*` and misses).
function pmFetchOk(url) {
  const ctrl = typeof AbortController === "function" ? new AbortController() : null;
  const t = ctrl ? setTimeout(() => ctrl.abort(), PRODUCT_MODAL.fetchTimeoutMs) : null;
  return fetch(url, {
    credentials: "same-origin",
    headers: { Accept: "text/html,application/xhtml+xml,application/xml;q=0.9,*/*;q=0.8" },
    signal: ctrl ? ctrl.signal : undefined,
  })
    .then((r) => {
      if (t) clearTimeout(t);
      if (!r.ok) throw new Error("HTTP " + r.status + " for " + url);
      return r.text();
    })
    .catch((err) => {
      if (t) clearTimeout(t);
      throw err;
    });
}

// ─── Overlay ────────────────────────────────────────────────────────────────
function pmEnsureOverlay() {
  // Build a FRESH overlay node on every open. We used to cache a single node and
  // just re-show it (display:none → block, new innerHTML), but on iOS Safari that
  // left WebKit unable to re-establish the touch-scroll layer for the reused,
  // composited (transform:translateZ(0)) container's position:fixed inner scroller
  // (.product-page-scrollable). Symptom: the FIRST swipe on the 2nd-and-later open
  // got eaten — images jog a few px and stop. The first open never showed it
  // because the node was genuinely new; recreating per open makes every open
  // identical to the first. Any stale node (e.g. left by an interrupted close) is
  // removed first. The close paths also remove the node, so this is normally a
  // straight create.
  const old = document.getElementById(PRODUCT_MODAL.overlayId);
  if (old) old.remove();
  const el = document.createElement("div");
  el.id = PRODUCT_MODAL.overlayId;
  el.style.cssText =
    "position:fixed;inset:0;z-index:var(--z-product-modal);background:#000;color:#fff;" +
    "overflow:hidden;display:none;transform:translateZ(0);";
  document.body.appendChild(el);
  return el;
}

// ─── Cache-miss loading animation ─────────────────────────────────────────────
// On a cold open the underlying view fades out (0.10s) and then there's nothing
// to look at until the fetch resolves — on a slow network that gap is long enough
// to read as "broken". Fill it with the same drawing-square animation the grid
// uses (snippets/loading.liquid → .loading-square, animated in style-globals).
// White on the black body background, dead-centre of the viewport. Shown only on
// a real cache MISS, and only after a short delay so warm/fast opens never flash
// it. Removed the instant content is injected, or on any failure/close path.
const PM_LOADING_MARKUP =
  '<div class="loading-square">' +
  '<div class="border left-drawing"></div>' +
  '<div class="border bottom-drawing"></div>' +
  '<div class="border top-drawing"></div>' +
  '<div class="border right-drawing"></div>' +
  "</div>";

function pmShowLoader() {
  if (document.getElementById(PRODUCT_MODAL.loaderId)) return;
  const l = document.createElement("div");
  l.id = PRODUCT_MODAL.loaderId;
  // Full-viewport, flex-centred, click-through. Sits at the product-modal layer
  // so it's above the faded-out underlay; the overlay (same z, appended later in
  // DOM) paints over it when content lands, and we remove it at that moment.
  l.style.cssText =
    "position:fixed;inset:0;z-index:var(--z-product-modal);" +
    "display:flex;align-items:center;justify-content:center;" +
    "pointer-events:none;";
  l.innerHTML = PM_LOADING_MARKUP;
  document.body.appendChild(l);
}

function pmHideLoader() {
  if (PRODUCT_MODAL.loaderTimer) {
    clearTimeout(PRODUCT_MODAL.loaderTimer);
    PRODUCT_MODAL.loaderTimer = null;
  }
  const l = document.getElementById(PRODUCT_MODAL.loaderId);
  if (l) l.remove();
}

// ─── Fade helpers ───────────────────────────────────────────────────────────
// Fade EVERY direct child of <body> (header, content, message, footer,
// collection-modal, etc.) at the same time, EXCEPT our product modal. Uses
// opacity only — no visibility:hidden or display:none, so the layout stays
// intact and there's no shift.
function pmCollectionElements() {
  // The entire top nav UI is excluded — filter-bar section, logo, and
  // cart-count all stay put through the collection-modal ↔ PDP transition.
  // Any content change inside the nav (e.g. logo visibility flip when the
  // PDP modal opens) is a snap, not a fade. Cart-count, if present, simply
  // persists at opacity 1 across both directions.
  //
  // #cart-drawer / #cart-backdrop are also excluded: they're body-level
  // cart chrome whose visibility is owned by HeaderState (the .cart-open
  // class). If the fade-out pinned them to inline opacity:0, that inline
  // value would beat the non-!important `body.cart-open #cart-drawer`
  // reveal rule, so opening the cart over the PDP modal would show nothing.
  return Array.from(document.body.children).filter(
    (el) =>
      el.id !== PRODUCT_MODAL.overlayId &&
      el.id !== "index-logo" &&
      el.id !== "cart-count" &&
      el.id !== "cart-drawer" &&
      el.id !== "cart-backdrop" &&
      el.id !== "cart-spacer-top" &&
      el.id !== "cart-spacer-bottom" &&
      el.id !== "shopify-section-index-filters" &&
      // Banner persists across the PDP modal until dismissed.
      el.id !== "shopify-section-index-message" &&
      // Nav background band is shown/faded via body.banner-open CSS, not the
      // modal fade — an inline opacity:0 here would beat that rule.
      el.id !== "nav-bg" &&
      el.tagName !== "SCRIPT"
  );
}

function pmFadeOutCurrentView(duration) {
  const els = pmCollectionElements();
  const dur = duration || "0.10s ease-out";
  els.forEach((el) => {
    el.style.transition = "opacity " + dur;
    el.style.opacity = "0";
  });
  return els;
}

function pmFadeInView(els) {
  if (!els || !els.length) return;
  els.forEach((el) => {
    el.style.transition = "opacity 0.9s ease-in-out";
    el.style.opacity = "1";
  });
  // Track this cleanup so a re-open within 950ms can cancel it.
  // Otherwise it would clear inline styles on the underlying view
  // we've just put back into "faded out" state for the next modal.
  if (PRODUCT_MODAL.fadeInTimer) clearTimeout(PRODUCT_MODAL.fadeInTimer);
  PRODUCT_MODAL.fadeInTimer = setTimeout(() => {
    PRODUCT_MODAL.fadeInTimer = null;
    els.forEach((el) => {
      el.style.transition = "";
      el.style.opacity = "";
    });
  }, 950);
}

// ─── Footer removal while the PDP is open ───────────────────────────────────
// The footer (sections/index-footer) renders as a body child ONLY on the index
// template — the one template the PDP modal opens over (a hard /products/x and
// the ?view=modal source both omit it via theme.liquid's gate / {% layout none %}).
// Requirement: while a product is shown the footer must not EXIST in the DOM —
// not merely opacity:0 / display:none. So we physically detach it on open and
// re-insert it at its EXACT prior slot on close. Capturing the slot makes this
// location-agnostic: the footer may be at home OR moved into a collection/filter
// modal by script-footer-visibility.js. The isConnected check on restore yields
// to any closer that already re-homed it (e.g. goHomeFromProductModal →
// __footerToHome), so we never double-insert or fight that path.
function pmRemoveFooter() {
  if (PRODUCT_MODAL._footerNode) return; // already detached (re-open while still open)
  const f = document.querySelector(".footer-wrapper");
  if (!f || !f.parentNode) return;
  PRODUCT_MODAL._footerNode = f;
  PRODUCT_MODAL._footerSlot = { parent: f.parentNode, next: f.nextSibling };
  f.remove();
}

function pmRestoreFooter() {
  const f = PRODUCT_MODAL._footerNode;
  const slot = PRODUCT_MODAL._footerSlot;
  PRODUCT_MODAL._footerNode = null;
  PRODUCT_MODAL._footerSlot = null;
  if (!f || f.isConnected) return; // gone, or a closer already re-homed it
  if (slot && slot.parent && slot.parent.isConnected) {
    if (slot.next && slot.next.parentNode === slot.parent) {
      slot.parent.insertBefore(f, slot.next);
    } else {
      slot.parent.appendChild(f);
    }
    // Re-measure against the view we're returning to. The footer node carries
    // whatever pin state it had when pmRemoveFooter detached it; without this
    // it can stay pinned (position:fixed) over the now-restored tall VIEW ALL /
    // collection grid, hovering until the next manual scroll (bugs P2/P3). The
    // underlying grid was laid out before the PDP opened, so the measurement is
    // already final here. (__footerToHome's branch below re-measures on its own.)
    if (typeof window.__footerUpdate === "function") window.__footerUpdate();
  } else if (document.body) {
    // The prior container is gone (e.g. its modal was torn down) — fall back to
    // the footer's home slot so the node is never left orphaned.
    document.body.appendChild(f);
    if (typeof window.__footerToHome === "function") window.__footerToHome();
  }
}

// ─── Scoped style block ─────────────────────────────────────────────────────
function pmEnsureStyle() {
  if (document.getElementById(PRODUCT_MODAL.styleId)) return;
  const s = document.createElement("style");
  s.id = PRODUCT_MODAL.styleId;
  s.textContent = `
    /* Re-anchor the index logo to the viewport while the modal is open.
       On the index template #index-logo is position:relative in body flow
       (occupies a ~50px slot); fixing it to top-left floats it over the PDP
       so it stays a live close target. The z-index scale already keeps the
       chrome band (--z-nav-logo) above the modal band (--z-product-modal),
       so this is just the repositioning + a visibility override — the filter
       bar sets inline visibility:hidden on #index-logo once active, so it
       needs !important. No opacity transition; the logo snaps in as the body
       class lands while the PDP overlay fades in beneath it. */
    body.product-modal-open #index-logo {
      position: fixed !important;
      top: 0 !important;
      left: 0 !important;
      z-index: var(--z-nav-logo) !important;
      visibility: visible !important;
      opacity: 1 !important;
      pointer-events: auto !important;
      cursor: pointer !important;
      transition: none !important;
    }

    /* When the cart opens over the modal, the lifted logo persists (it's
       already the modal's close affordance) — don't hide/swap it. Just route
       pointer events: container none (negative space → #cart-close-bar →
       close), the logo image re-enables them (→ home) via the body.cart-open
       rule in navigation.liquid. */
    body.product-modal-open.cart-open #index-logo {
      pointer-events: none !important;
    }

    /* Filter bar instantly swaps out when the PDP opens — no fade.
       The class lands synchronously with the click in openProductModal,
       so the bar's contents (FILTER label, VIEW ALL, NAME/CATEGORY/FINISH
       dropdowns) disappear in the same frame the logo appears, while the
       PDP body fades in separately over FADE_IN_MS. Reverse on close:
       removing the class snaps the filter bar back instantly. */
    /* display:none, not visibility/opacity — the dropdowns force their own
       pointer-events, so visibility:hidden alone leaves them clickable behind
       the PDP. The swap is instant (no fade), so removing it from the box model
       is correct and kills all interactivity. */
    body.product-modal-open #index-menu {
      display: none !important;
    }

    /* Keep the filter bar gone even when the cart opens over the modal. */
    body.product-modal-open.cart-open #index-menu {
      display: none !important;
    }

    /* Restore pointer-events on the × icon when the cart is open over the PDP.
       The normal close path — click passes through #cart-count (pointer-events:none)
       → falls to #index-menu (visibility:hidden, can't receive events) → no close.
       With pointer-events restored, the toggle handler in script-cart.js fires
       directly and closes the cart. */
    body.product-modal-open.cart-open #cart-count {
      pointer-events: auto !important;
      cursor: pointer !important;
    }

    /* No cart-over-modal override needed: the z-index scale puts the cart
       band (--z-cart-backdrop / --z-cart-drawer) above the modal band
       (--z-product-modal), so the cart opens on top of the PDP modal by
       design. See the scale in assets/style-globals.css.liquid. */

    /* Description container — keep style-product.css's position:fixed so it
       doesn't scroll with the grid. The modal's transform:translateZ(0)
       provides the containing block, so it anchors to the modal (not the raw
       viewport) and remains inside the modal's stacking context. */
    #${PRODUCT_MODAL.overlayId} .product-description-container {
      position: fixed !important;
      z-index: 100 !important;
      pointer-events: auto !important;
    }

    /* Force every text container and the text elements visible in every
       way that could be hidden by inherited/cascaded rules. This is the
       only way to be sure no upstream rule (CSS, script-set inline style,
       script-product-* state, opacity:0, visibility:hidden, font-size:0,
       transparent color) is suppressing the actual rendering. */
    #${PRODUCT_MODAL.overlayId} .product-page-container-right,
    #${PRODUCT_MODAL.overlayId} .product-description-container,
    #${PRODUCT_MODAL.overlayId} .product-sku-title-container,
    #${PRODUCT_MODAL.overlayId} .sku-expand,
    #${PRODUCT_MODAL.overlayId} .product-title-container,
    #${PRODUCT_MODAL.overlayId} .description-dimensions-weight-manufacturing,
    #${PRODUCT_MODAL.overlayId} .product-description-en-jp,
    #${PRODUCT_MODAL.overlayId} .description-text,
    #${PRODUCT_MODAL.overlayId} .description-text-jp,
    #${PRODUCT_MODAL.overlayId} .description-text p,
    #${PRODUCT_MODAL.overlayId} .description-text-jp p,
    #${PRODUCT_MODAL.overlayId} .dimensions,
    #${PRODUCT_MODAL.overlayId} .weight,
    #${PRODUCT_MODAL.overlayId} .manufacturing-location {
      opacity: 1 !important;
      visibility: visible !important;
      color: #fff !important;
    }
    #${PRODUCT_MODAL.overlayId} .description-text,
    #${PRODUCT_MODAL.overlayId} .description-text-jp,
    #${PRODUCT_MODAL.overlayId} .description-text p,
    #${PRODUCT_MODAL.overlayId} .description-text-jp p {
      display: block !important;
      font-size: 13px !important;
      line-height: 1.1 !important;
    }
    @media (min-width: 701px) {
      #${PRODUCT_MODAL.overlayId} .description-dimensions-weight-manufacturing {
        display: flex !important;
      }
      #${PRODUCT_MODAL.overlayId} .product-title-container {
        display: block !important;
      }
      #${PRODUCT_MODAL.overlayId} .product-description-en-jp {
        display: flex !important;
      }
    }

    /* White text against the black modal background. Targets the actual
       text-bearing elements only — does not touch buttons or outlines. */
    #${PRODUCT_MODAL.overlayId} .description-text,
    #${PRODUCT_MODAL.overlayId} .description-text-jp,
    #${PRODUCT_MODAL.overlayId} .description-text p,
    #${PRODUCT_MODAL.overlayId} .description-text-jp p,
    #${PRODUCT_MODAL.overlayId} .product-title-en,
    #${PRODUCT_MODAL.overlayId} .product-title-jp,
    #${PRODUCT_MODAL.overlayId} .sku-expand,
    #${PRODUCT_MODAL.overlayId} .sku-expand p,
    #${PRODUCT_MODAL.overlayId} .weight,
    #${PRODUCT_MODAL.overlayId} .dimensions,
    #${PRODUCT_MODAL.overlayId} #dimensions-display,
    #${PRODUCT_MODAL.overlayId} .manufacturing-location,
    #${PRODUCT_MODAL.overlayId} .selected-variant,
    #${PRODUCT_MODAL.overlayId} .add-to-cart-text,
    #${PRODUCT_MODAL.overlayId} .sold-out-text,
    #${PRODUCT_MODAL.overlayId} .price,
    #${PRODUCT_MODAL.overlayId} .jp-font-close {
      color: #fff !important;
    }
  `;
  document.head.appendChild(s);
}

// ─── style-product.css (only loaded while modal is open) ────────────────────
// Returns a Promise that resolves when the stylesheet has loaded, so we can
// inject the product markup AFTER its CSS is applied (preventing FOUC where
// images flash at default browser size before shrinking to their styled size).
function pmEnsureProductCss(linkFromFetched) {
  if (!linkFromFetched) return Promise.resolve();
  const existing = document.getElementById(PRODUCT_MODAL.cssId);
  if (existing) return Promise.resolve();
  return new Promise((resolve) => {
    const link = document.createElement("link");
    link.id = PRODUCT_MODAL.cssId;
    link.rel = "stylesheet";
    link.href = linkFromFetched.getAttribute("href");
    link.onload = resolve;
    link.onerror = resolve;
    document.head.appendChild(link);
  });
}

// ─── Parse fetched product page ─────────────────────────────────────────────
function pmExtract(html) {
  const doc = new DOMParser().parseFromString(html, "text/html");
  const wrapper = doc.querySelector(".product-page-wrapper");
  if (!wrapper) return null;

  // Eager-load product images. Shopify's product template marks them
  // `loading="lazy"`, which delays the fetch until layout decides the
  // image is near the viewport — fine on a real PDP, wrong inside a
  // modal that is the user's current focus. Also start each image at
  // opacity 0 with a short fade transition so it can be revealed
  // individually on its own load event (chrome doesn't have to wait).
  wrapper.querySelectorAll("img").forEach((img, idx) => {
    img.removeAttribute("loading");
    img.setAttribute("decoding", "async");
    if (idx === 0) img.setAttribute("fetchpriority", "high");
    const existing = img.getAttribute("style") || "";
    // Image fades in on its own `load` event, matched to the overlay
    // timing so a late-arriving image lands with the same easing as
    // the chrome fade-in.
    img.setAttribute(
      "style",
      existing + ";opacity:0;transition:opacity 0.5s ease-out;"
    );
    img.dataset.pmFadeImg = "1";
  });

  const scripts = [];
  doc.querySelectorAll("script").forEach((s) => {
    const src = s.getAttribute("src") || "";
    if (
      src.indexOf("script-product-image-modal") !== -1 ||
      src.indexOf("script-product-variants") !== -1 ||
      src.indexOf("script-product-standard") !== -1
    ) {
      scripts.push({ external: true, src: s.src });
    } else if (!s.src && s.textContent.indexOf("const variants") !== -1) {
      scripts.push({ external: false, text: s.textContent });
    }
  });

  return {
    wrapper: wrapper.outerHTML,
    imageExpand: doc.querySelector("#image-expand")?.outerHTML || "",
    closeIcon: doc.querySelector(".IMAGE_MODAL_CL_CLOSE")?.outerHTML || "",
    styleLink: doc.querySelector('link[href*="style-product.css"]'),
    scripts,
  };
}

// ─── Re-execute product scripts inside the modal ────────────────────────────
// DOMContentLoaded has already fired on the parent page, so handlers the
// product scripts register with that event name would never run. We fetch
// each script body as text, swap "DOMContentLoaded" → our custom event name,
// inject as an inline <script> (which executes on append), then dispatch the
// custom event manually. Inline `const variants = {...}` is injected as-is so
// the binding is visible to the rewritten variants handler.
function pmRunScripts(scripts) {
  // UNIQUE ready-event name per open. Each injected product script has its
  // "DOMContentLoaded" rewritten to THIS name, and we dispatch only THIS name —
  // so only the CURRENT product's freshly-injected handlers run.
  //
  // Why this matters: injected <script> elements register their ready-event
  // listeners on `document` and are never unregistered across opens. With a
  // single shared event name, every previously-opened product's handlers fired
  // again on each open. The damaging case: open a NO-VARIANT product (injects
  // script-product-standard.js, which registers an instruction-manual handler),
  // then open a VARIANT product (injects script-product-variants.js). On the
  // variant open the shared event fired BOTH scripts' handlers; standard.js's
  // ran first, bound to the variant product's instruction button, and set
  // dataset.imBound="1" — so variants.js's correct handler bailed. The variant
  // button was left wired to the standard handler, which is blind to
  // #variant-images → product/variant/instruction shown side-by-side (the
  // duplication bug). Per-open event names mean stale handlers never fire again.
  const readyEvent = PRODUCT_MODAL.readyEvent + ":" + (++PRODUCT_MODAL._openSeq);

  // Drop the previous open's injected <script> nodes so the DOM doesn't grow
  // unbounded. (Removing a <script> doesn't unregister its listeners, but those
  // are now inert — they listen on a stale, never-re-dispatched event name.)
  document
    .querySelectorAll("script[data-pm-product-script]")
    .forEach((s) => s.remove());

  const fetches = scripts.map((def) => {
    if (!def.external) return Promise.resolve(def);
    return pmFetchOk(def.src).then((text) => ({ external: true, text }));
  });

  return Promise.all(fetches).then((resolved) => {
    resolved.forEach((item, i) => {
      const source = item.external
        ? item.text
            .replace(/"DOMContentLoaded"/g, `"${readyEvent}"`)
            .replace(/'DOMContentLoaded'/g, `'${readyEvent}'`)
        : // The inline product script declares `const variants = {...}` at top
          // level. Injected as a classic <script>, that lands in the SHARED
          // global lexical scope — so the 2nd modal open throws "Identifier
          // 'variants' has already been declared", the script aborts, and
          // `variants` stays stuck on the FIRST product. Selecting a variant on
          // a later-opened modal then looks up an id that isn't there and
          // updateVariantDetails throws → the page stops updating. Rewriting to
          // `var` makes re-injection reassign the latest product's data instead.
          item.text.replace(/\bconst\s+variants\b/, "var variants");

      // Validate syntax before injecting. Parse errors in inline scripts
      // (e.g. `const variants = {...}` when a variant title contains an
      // unescaped quote) would otherwise silently break all downstream
      // scripts that depend on the `variants` binding.
      try {
        new Function(source);
      } catch (err) {
        console.error(
          "[product-modal] Script " + i + " failed to parse:",
          err.message,
          "\n--- offending source (first 500 chars) ---\n" + source.slice(0, 500)
        );
        return; // skip this script
      }

      const s = document.createElement("script");
      s.textContent = source;
      s.setAttribute("data-pm-product-script", "1"); // so the next open can prune it
      document.body.appendChild(s);
    });
    document.dispatchEvent(new Event(readyEvent));
  });
}

// ─── Open ───────────────────────────────────────────────────────────────────
// Fade out the ENTIRE collection view (0.10s ease-out), then show the modal
// and fade in the ENTIRE PDP content (0.9s ease-in-out). Matches the index ↔
// collection-modal transition timing exactly.
function openProductModal(productUrl, opts) {
  opts = opts || {};
  // If a close from a previous PDP is still mid-flight, its 100ms timer
  // would later wipe the freshly-opened overlay (display:none + innerHTML
  // = ""), and its fadeIn timer would restore the underlying view's
  // inline opacity right as we're fading the new modal in. Cancel both
  // and finalize the close synchronously so we start from a clean slate.
  if (PRODUCT_MODAL.closeTimer) {
    clearTimeout(PRODUCT_MODAL.closeTimer);
    PRODUCT_MODAL.closeTimer = null;
    // Remove the prev overlay outright; pmEnsureOverlay below builds a fresh one
    // (reused nodes break iOS touch scrolling — see pmEnsureOverlay).
    const prevOverlay = document.getElementById(PRODUCT_MODAL.overlayId);
    if (prevOverlay) prevOverlay.remove();
    // Intentionally DO NOT tear down the body scroll-lock, .product-modal-open
    // class, style-product.css link, or pmEnsureStyle block here. The next
    // state is "modal open again" — releasing the lock now would re-introduce
    // the body scrollbar for the duration of the fetch (~200–500ms), which
    // narrows the viewport by ~15px and visibly shrinks the underlying
    // collection-modal grid (4 columns × aspect-ratio:1 → each item loses
    // ~3.75px both axes, then snaps back when the lock is re-applied at
    // lines 443-448). The CSS/style blocks are idempotent — pmEnsureStyle
    // and pmEnsureProductCss skip re-adding when their elements exist —
    // so leaving them in place is correct, not a leak.
  }
  if (PRODUCT_MODAL.fadeInTimer) {
    clearTimeout(PRODUCT_MODAL.fadeInTimer);
    PRODUCT_MODAL.fadeInTimer = null;
  }
  // Clear any loader/loader-timer left by a previous (possibly interrupted) open.
  pmHideLoader();

  const overlay = pmEnsureOverlay();
  pmEnsureStyle();

  // Match the index → collection-modal animation pattern (animateAndOpen):
  //   1. Fade the underlying view out fast (0.10s ease-out).
  //   2. Once it's gone, fade the new view in slowly (0.9s ease-in-out).
  // Sequential, not a crossfade.
  const FADE_OUT = "0.10s ease-out";
  const FADE_OUT_MS = 100;
  // ease-out (not ease-in-out) so content becomes visible immediately
  // and settles gently. With ease-in-out at 0.9s, the first ~200ms
  // was nearly transparent — the eye perceived a delay even though
  // the animation had technically started.
  const FADE_IN = "0.5s ease-out";
  const FADE_IN_MS = 500;

  // Scroll-lock the underlying document. The bug this kills: after the home reveal
  // settles, the document sits at a non-zero scroll (script-index-content.js
  // auto-scrolls 25% on load: window.scrollTo(0, innerHeight*0.25)); you open the
  // PDP and the first swipe on the inner scroller (.product-page-scrollable) jogs a
  // few px then abruptly stops — the DOCUMENT steals it. User pinned the trigger
  // precisely: "let the grid fade-in finish, THEN open, THEN scroll" gates it;
  // interrupting the reveal dodges it.
  //
  // ROOT CAUSE (iOS Safari only): manipulating `overflow` on mobile breaks the
  // touch-scroll context of the freshly-built inner scroller — the SAME lesson the
  // reveal code records ("Avoid manipulating overflow on mobile — it breaks iOS
  // touch scroll context", script-index-content.js). So the old `overflow:hidden`
  // lock here was itself the cause: it broke the inner scroller's context, and the
  // document (at the 25% scroll) grabbed the gesture. Android Chrome is immune.
  //
  // FIX, per platform:
  //  • Touch (pointer:coarse) — do NOT touch overflow at all. Pin <body> with
  //    position:fixed; top:-Y instead: a real lock (body out of flow → document
  //    unscrollable) that leaves the inner scroller's touch context intact. top:-Y
  //    keeps every fixed/absolute element in its exact viewport spot (no jump);
  //    window.scrollTo(0,Y) on close puts the document back (real scroll is 0 while
  //    pinned). While pinned window.scrollY reads 0, so two readers use _scrollLockY
  //    instead: the close-time logo check below, and HeaderState.positionCartLayer.
  //  • Desktop — no touch scroller to break, and position:fixed+width risks a
  //    scrollbar-gutter shift, so keep the plain overflow:hidden lock.
  //
  // Capture prior overflows + live scrollY ONCE, guarded so the interrupt re-open
  // path (keeps the lock across close→reopen, scrollY already 0) can't clobber them.
  const _pmFirstLock = document.documentElement.dataset.pmRootLock !== "1";
  if (_pmFirstLock) {
    PRODUCT_MODAL._htmlOverflowPrev = document.documentElement.style.overflow;
    PRODUCT_MODAL._bodyOverflowPrev = document.body.style.overflow;
    PRODUCT_MODAL._scrollLockY = window.scrollY || window.pageYOffset || 0;
    document.documentElement.dataset.pmRootLock = "1";
  }
  if (window.matchMedia && window.matchMedia("(pointer: coarse)").matches) {
    var _pmLockY = PRODUCT_MODAL._scrollLockY;
    document.body.style.position = "fixed";
    document.body.style.top = -_pmLockY + "px";
    document.body.style.left = "0";
    document.body.style.right = "0";
    document.body.style.width = "100%";
  } else {
    // Pin the document's height to its pre-open value BEFORE the lock + the
    // .product-modal-open class land. Opening hides the footer (.footer-wrapper
    // display:none, plus pmRemoveFooter), which SHRINKS the document. Unlike the
    // touch path (body position:fixed → out of flow, can't shrink-clamp), the
    // desktop overflow:hidden lock leaves the document in flow, so on a short home
    // grid scrolled near the bottom the browser clamps scrollY up to the new max —
    // the underlying grid visibly jumps DOWN during the fade-out (diagnosed:
    // scrollY 239→182, grid offsetTop unchanged). Holding body min-height at the
    // current scrollHeight keeps the max-scroll fixed so nothing clamps. Captured
    // only on the first lock (an interrupt re-open must not overwrite the saved
    // value); restored on close. Safe with html { scrollbar-gutter: stable }.
    if (_pmFirstLock) {
      PRODUCT_MODAL._bodyMinHeightPrev = document.body.style.minHeight || "";
      document.body.style.minHeight = document.documentElement.scrollHeight + "px";
    }
    document.documentElement.style.overflow = "hidden";
    document.body.style.overflow = "hidden";
  }

  // On the index template #index-logo is `position:relative` and occupies
  // a ~50px slot at the top of body flow. Adding .product-modal-open flips
  // it to position:fixed, removing that slot — which shifts every section
  // below it (filter bar, content, grid) upward by the logo's height in
  // the same frame the fade-out starts. When the open is triggered from
  // the bare index (no collection-modal covering the body), the shift is
  // visible. Compensate by parking the equivalent height as body padding
  // BEFORE the class lands, then strip it on close. Stored on the dataset
  // so close can restore the previous inline value exactly.
  const indexLogoEl = document.getElementById("index-logo");
  if (
    indexLogoEl &&
    getComputedStyle(indexLogoEl).position === "relative" &&
    !document.body.dataset.pmPadTopPrev
  ) {
    const logoH = indexLogoEl.getBoundingClientRect().height;
    if (logoH > 0) {
      document.body.dataset.pmPadTopPrev = document.body.style.paddingTop || "";
      document.body.style.paddingTop = logoH + "px";
    }
  }

  document.body.classList.add("product-modal-open");

  // Logo and cart-count are excluded from pmCollectionElements above, so
  // they don't fade with the rest of the underlying view. Any state change
  // on them across the transition (e.g. logo visibility flipping from
  // hidden → visible via body.product-modal-open) is a snap, not a fade.

  // 1. Fade-out starts NOW, synchronously with the click.
  const fadedView = pmFadeOutCurrentView(FADE_OUT);

  // Update the URL to the product page IMMEDIATELY (synchronously with the
  // click), not after the fetch resolves. If the open is slow and the user
  // refreshes before content lands, they get the real product page rather
  // than a stale index URL. Snapshot the pre-open URL so the failure paths
  // below can roll it back. _pmPushedUrl marks that we own this entry.
  const _pmPrevUrl = window.location.pathname + window.location.search;
  // opts.fromPopstate: the open is being driven by a back/forward navigation
  // INTO an existing productModal history entry. The browser already moved the
  // address bar to productUrl — pushing again would stack a duplicate entry (and
  // break a subsequent forward). Only push when this is a fresh, user-initiated open.
  if (!opts.fromPopstate) {
    history.pushState({ productModal: true, url: productUrl }, "", productUrl);
  }
  PRODUCT_MODAL._pmPushedUrl = productUrl;

  // 2. Fetch (or cache hit) runs in parallel.
  // Only treat a string in the cache as a real hit. In-flight Promises
  // from pmPrefetch live there too and should be awaited (not re-fetched).
  const cached = PRODUCT_MODAL.cache[productUrl];
  const htmlPromise =
    typeof cached === "string"
      ? Promise.resolve(cached)
      : cached && typeof cached.then === "function"
        ? cached
        : pmFetchOk(pmModalUrl(productUrl)).then((html) => {
            PRODUCT_MODAL.cache[productUrl] = html;
            return html;
          });

  // Cold open (no ready HTML in cache): the content has to come over the wire,
  // which can leave a visible gap after the underlay fades out. Schedule the
  // centred loading animation, but only after a short delay — a fast response
  // beats the timer and pmHideLoader() (called on inject/failure) cancels it, so
  // warm or quick opens never flash it.
  if (typeof cached !== "string") {
    PRODUCT_MODAL.loaderTimer = setTimeout(pmShowLoader, 150);
  }

  // Cross-fade. We used to wait for the 100ms grid fade-out before
  // injecting, which added pure latency on warm-cache opens. Now the
  // grid fades out and the overlay fades in concurrently — eye sees
  // the new content begin to appear within a frame of the click.
  htmlPromise
    .then((html) => {
      const data = pmExtract(html);
      if (!data) return null;

      // AWAIT style-product.css before injecting the markup. The stylesheet
      // is what positions .product-description-container as position:fixed
      // on the right side of the layout; without it the metafield block
      // (description, dimensions, weight, manufacturing-location, JP text)
      // paints in default static flow, then jumps to its final slot when
      // the stylesheet lands mid-fade. Preloaded from pre-load.liquid on
      // index templates, so on a warm cache this resolves immediately.
      return pmEnsureProductCss(data.styleLink).then(() => data);
    })
    .then((data) => {
      if (!data) {
        pmHideLoader();
        // Extraction failed and we never opened — roll the URL back to where
        // the user was so the address bar isn't left on a page we didn't show.
        if (PRODUCT_MODAL._pmPushedUrl === productUrl) {
          PRODUCT_MODAL._pmPushedUrl = null;
          try { history.replaceState({}, "", _pmPrevUrl); } catch (_) {}
        }
        return;
      }

      // Content is here — drop the loading animation before we paint the PDP.
      pmHideLoader();

      // Inject content while overlay is at opacity 0 so description and
      // surrounding chrome are fully painted before the fade-in begins.
      overlay.style.display = "block";
      overlay.style.opacity = "0";
      overlay.style.transition = "";
      overlay.innerHTML = data.imageExpand + data.closeIcon + data.wrapper;

      // Now that the modal is committed to opening, take the index footer OUT of
      // the DOM entirely (restored on close). Done here — not earlier — so a
      // failed fetch (which returns before this point) never strands the footer.
      pmRemoveFooter();

      // Chrome (logo, cart, title, description, dimensions) is now in the
      // DOM at opacity 0; the overlay fade below brings it in immediately.
      // Images, however, start at opacity:0 from pmExtract and only fade
      // in when their bytes arrive — so the text-y content doesn't have
      // to wait for image decode to feel "loaded".
      overlay.querySelectorAll("img[data-pm-fade-img]").forEach((img) => {
        // Show the loading animation (snippets/loading.liquid → .image-loading,
        // default display:none) while this image's bytes are in flight, then
        // hide it on load. initializeImageLoading() in script-functions.js does
        // this for statically-rendered pages but never runs for the injected
        // modal, so wire it up here — same per-image behavior as the grid.
        const loader = img
          .closest(".img-and-loading-container, .media-and-loading-container")
          ?.querySelector(".image-loading");
        const reveal = () => {
          img.style.opacity = "1";
          if (loader) loader.style.display = "none";
        };
        if (img.complete && img.naturalWidth > 0) {
          reveal();
        } else {
          if (loader) loader.style.display = "flex";
          img.addEventListener("load", reveal, { once: true });
          img.addEventListener("error", reveal, { once: true });
        }
      });

      // URL was already pushed synchronously at the top of openProductModal
      // (instant refresh-safety); nothing to do here. Clear the ownership marker
      // now the open has succeeded.
      PRODUCT_MODAL._pmPushedUrl = null;

      // Run product scripts before the fade-in so variants / zoom / image
      // modal are wired up the moment the PDP becomes visible.
      pmRunScripts(data.scripts);

      // Scroll lock + .product-modal-open class were applied synchronously
      // at the top of openProductModal, before the fade-out started, so the
      // viewport width is already stable by this point.

      // The hidden-state snapshot (logoWasHidden / cartWasHidden) and the
      // opacity:0 pin were applied SYNCHRONOUSLY at the top of openProductModal,
      // before the fetch started, so the elements are already at opacity:0
      // by the time we get here — no flash window.

      // 3. Fade the overlay in. Logo and cart-count are not animated —
      //    they persist (or snap, if state changed) across the transition.
      requestAnimationFrame(() => {
        overlay.style.transition = "opacity " + FADE_IN;
        overlay.style.opacity = "1";
      });

      PRODUCT_MODAL._fadedView = fadedView;
    })
    .catch((err) => {
      console.warn("[product-modal] open failed, falling back to navigation:", err && err.message);
      pmHideLoader();
      // Poisoned/failed cache entry must not survive — next click should retry.
      delete PRODUCT_MODAL.cache[productUrl];
      // Restore the underlying view in case navigation is slow or cancelled.
      if (fadedView) {
        fadedView.forEach((el) => {
          el.style.transition = "";
          el.style.opacity = "";
        });
      }
      // Hard navigate so the user still reaches the product page rather
      // than being stuck on a black screen.
      try { window.location.href = productUrl; } catch (_) {}
    });
}

// ─── Close ──────────────────────────────────────────────────────────────────
// Fade the ENTIRE PDP out (0.10s), then hide it and fade the collection
// view back in (0.9s ease-in-out). Mirrors the open transition in reverse.
function closeProductModal(goBack) {
  // If a back-navigation lands mid cold-open (overlay still hidden, loader up),
  // make sure the animation doesn't get stranded on screen.
  pmHideLoader();
  const overlay = document.getElementById(PRODUCT_MODAL.overlayId);
  if (!overlay || overlay.style.display === "none") return;

  // Re-entry guard. The logo click calls closeProductModal(true) which
  // calls history.back(), and our popstate handler then calls
  // closeProductModal(false). Without this, the second call would
  // capture a now-null _fadedView and the underlying view would never
  // fade back in (black screen). If a close is already pending, only
  // honor the goBack request and bail.
  if (PRODUCT_MODAL.closeTimer) {
    if (goBack) history.back();
    return;
  }

  const fadedView = PRODUCT_MODAL._fadedView;
  PRODUCT_MODAL._fadedView = null;

  overlay.style.transition = "opacity 0.10s ease-out";
  overlay.style.opacity = "0";

  // Restore the underlying view's CHROME synchronously (not in the 100ms
  // teardown timer below). product-modal-open forces the logo as the PDP's
  // close affordance; if we wait the full teardown to drop it, the collection
  // re-appears with the logo still showing and only swaps to the collection
  // title when the timer fires — the visible "logo, then title" flash when
  // back-swiping. Dropping the class + restoring logo/padding now means the
  // header is already in its collection-title state from the first frame, while
  // the PDP overlay fades out on top (chrome z-index sits above the PDP band).
  // Safe to do early: #index-logo's base is position:fixed, so reverting the
  // pmEnsureStyle override causes no layout shift, and the style block's rules
  // are all `body.product-modal-open`-gated, so they no-op the instant the
  // class is gone (the block itself is removed in the timer).
  document.body.classList.remove("product-modal-open");
  if ("pmPadTopPrev" in document.body.dataset) {
    document.body.style.paddingTop = document.body.dataset.pmPadTopPrev;
    delete document.body.dataset.pmPadTopPrev;
  }
  // The bar is back in the box model now (product-modal-open's display:none on
  // #index-menu is gone). Re-measure the filter-group widths: on the
  // logo→home path, goHomeFromProductModal already ran clearAllFilters() while
  // the bar was still display:none, so any group reset to its placeholder kept
  // its stale pinned width (offsetWidth read 0 → width-write was skipped). Left
  // uncorrected, a NAME group pinned to a long collection title stays that wide
  // behind the short "NAME" label — a phantom gap that snaps closed on the next
  // re-measure (the layout shift). measureAndSetWidths only touches groups
  // showing their placeholder, so pinned/hidden groups (back-to-collection) are
  // untouched.
  if (window.__measureFilterWidths) window.__measureFilterWidths();
  const indexLogo = document.getElementById("index-logo");
  if (indexLogo) {
    indexLogo.style.transition = "";
    // The logo shows ONLY at the home top with nothing else in the bar. Keep it
    // HIDDEN whenever the filter bar is the nav content — i.e. a collection modal
    // is open OR the home grid is scrolled (direct-link products open the PDP
    // without a collection, so the scroll check is what catches that case). Else
    // it overlaps the bar.
    var colModalEl = document.getElementById("collection-modal");
    var collectionOpen = colModalEl && colModalEl.style.display === "block";
    // Body is still pinned here (released in the 100ms teardown), so window.scrollY
    // reads 0 — use the captured underlay scroll instead.
    var scrolled = (PRODUCT_MODAL._scrollLockY || 0) > 0;
    if (collectionOpen || scrolled) {
      indexLogo.style.opacity = "0";
      indexLogo.style.visibility = "hidden";
    } else {
      indexLogo.style.opacity = "";
      indexLogo.style.visibility = "";
    }
  }
  const cartCount = document.getElementById("cart-count");
  if (cartCount) {
    cartCount.style.transition = "";
    cartCount.style.opacity = cartCount.textContent.trim() === "" ? "0" : "1";
  }

  // Track the timer so a quick re-open can cancel it. Without this, if the
  // user opens a second product within 100ms of closing, the stale timeout
  // wipes the new overlay (display:none + innerHTML = "") → black screen.
  if (PRODUCT_MODAL.closeTimer) clearTimeout(PRODUCT_MODAL.closeTimer);
  PRODUCT_MODAL.closeTimer = setTimeout(() => {
    PRODUCT_MODAL.closeTimer = null;
    // Remove (not just hide) the overlay so the next open builds a fresh node —
    // see pmEnsureOverlay for why reuse broke iOS touch scrolling. The fade-out
    // (0.10s) has finished by the time this 100ms timer fires, so there's no pop.
    overlay.remove();
    // Release the body pin (touch path), THEN restore the document scroll. While
    // pinned the real document scroll was 0, so scrollTo(0,Y) is what puts the
    // underlay back where it was at open. The underlay is still opacity:0 here
    // (pmFadeInView below starts the fade), so even if there were a paint between
    // unpin and scroll it would be invisible — no jump.
    document.body.style.position = "";
    document.body.style.top = "";
    document.body.style.left = "";
    document.body.style.right = "";
    document.body.style.width = "";
    // Restore overflow to EXACTLY what each had before the lock (not "") so we don't
    // strip the reveal's `overflow:auto` on mobile, or any value another script owns.
    // On the touch path these were never changed, so this is a no-op there.
    document.body.style.overflow = PRODUCT_MODAL._bodyOverflowPrev || "";
    document.documentElement.style.overflow = PRODUCT_MODAL._htmlOverflowPrev || "";
    delete document.documentElement.dataset.pmRootLock;
    PRODUCT_MODAL._htmlOverflowPrev = "";
    PRODUCT_MODAL._bodyOverflowPrev = "";
    var _pmRestoreY = PRODUCT_MODAL._scrollLockY || 0;
    PRODUCT_MODAL._scrollLockY = 0;
    window.scrollTo(0, _pmRestoreY);
    document.getElementById(PRODUCT_MODAL.cssId)?.remove();
    document.getElementById(PRODUCT_MODAL.styleId)?.remove();

    // Put the footer back where it came from BEFORE the fade-in restores opacity
    // (pmFadeInView re-sets opacity:1 on the same node if it's part of fadedView).
    pmRestoreFooter();

    // Release the pinned min-height ONLY after the footer is back (so the document
    // is naturally full height again and removing the floor can't re-shrink it and
    // re-clamp the just-restored scroll). No-op on the touch path (never set).
    document.body.style.minHeight = PRODUCT_MODAL._bodyMinHeightPrev || "";
    PRODUCT_MODAL._bodyMinHeightPrev = "";

    pmFadeInView(fadedView);
  }, 100);

  if (goBack) history.back();
}

window.openProductModal = openProductModal;
window.closeProductModal = closeProductModal;

// Close the PDP and the collection/filter modal beneath it, landing on the bare
// home index-grid at the TOP. Used when the user clicks anywhere on the
// #index-logo strip while a PDP is open — the logo's "go home" semantics,
// matching every other template.
function goHomeFromProductModal() {
  const colModal = document.getElementById("collection-modal");
  const underlayOpen = colModal && colModal.style.display === "block";

  // Going HOME always lands at the TOP of the bare home grid, never wherever the
  // home grid happened to be scrolled when the PDP was opened — that's what made
  // the logo feel inconsistent. Pin BOTH saved scrolls to 0 up front, before any
  // restore below reads them:
  //  • __indexHomeScrollY — the instant closers call scrollTo(0, __indexHomeScrollY)
  //    + restoreHeaderOpacity (which reads scrollY), so it must resolve to the top.
  //  • _scrollLockY — closeProductModal's pin-release does scrollTo(0, _scrollLockY)
  //    and its close-time logo check treats _scrollLockY > 0 as "scrolled".
  window.__indexHomeScrollY = 0;
  PRODUCT_MODAL._scrollLockY = 0;

  // Drive the nav chrome to its home-TOP state — centred logo shown, filter bar
  // hidden — via HeaderState.exitModal's at-top restore branch. atTop is forced
  // true (not derived from the old scroll) because we always land at the top now.
  // _modalOpenedFromProductClick gates that branch, so set it even for a modal
  // opened some OTHER way (e.g. a deep-link hard-refresh on ?collection=); without
  // it exitModal would leave the bar up AND the logo showing over it (the overlap
  // bug). exitModal CONSUMES both flags, so set them once here and let exactly one
  // path below invoke it.
  window._modalOpenedFromProductClick = true;
  window._preProductClickBarState = { atTop: true };

  if (underlayOpen) {
    // Swap the underlay to the home grid INSTANTLY while the opaque PDP overlay
    // still covers everything, so the collection we came from is never seen. The
    // closers use the no-animation (popstate-style) reset and each call
    // HeaderState.exitModal() internally (consuming the flags above). If we closed
    // the PDP first, its fade-in would reveal the collection and only then animate
    // it away — the "collection flashes, then home" we're avoiding.
    const fbar = document.getElementById("filter-bar");
    const filterActive = fbar && (
      fbar.classList.contains("name-active") ||
      fbar.classList.contains("category-finish-active") ||
      fbar.classList.contains("viewall-active")
    );
    if (filterActive && typeof window.closeFilterModal === "function") {
      window.closeFilterModal();              // instant
    } else if (typeof window.closeCollectionModal === "function") {
      window.closeCollectionModal(false);     // instant
    }
  } else if (window.HeaderState && typeof window.HeaderState.exitModal === "function") {
    // PDP opened DIRECTLY from the home grid (a direct-link product, no collection
    // underlay): no closer runs, so nothing consumes the flags or resets the
    // chrome. Drive the same at-top restore here — otherwise a home grid that was
    // scrolled when the PDP opened comes back with the filter bar showing instead
    // of the centred logo.
    window.HeaderState.exitModal();
  }

  // Now fade the PDP out — its pin-release restores the document to the home TOP
  // (_scrollLockY is 0), revealing the already-reset home grid directly.
  closeProductModal(false);

  // Normalize the URL back to the bare index "/". Must be a literal "/", NOT
  // window.location.pathname: openProductModal pushState'd the product URL, so
  // by now the path IS "/products/x" — reading it back would just re-write the
  // product URL and the logo-home would never clear it. The instant closers above
  // run the updateHistory:false branch (no history write), so this is the only
  // normalization. script-index-product.js only loads on the index template
  // (via index-grid.liquid), so home is unambiguously "/".
  try { history.replaceState({}, "", "/"); } catch (_) {}
}

// While the PDP modal is open, clicking ANYWHERE on the full-width #index-logo
// strip (the PDP's close affordance) goes all the way HOME — the logo's "go
// home" semantics, matching every other template. There is no longer an
// image-vs-negative-space split: previously a click on the logo IMAGE went home
// while a click on the surrounding padding did history.back() to the collection,
// so "clicking the logo" landed home OR back-on-collection depending on the exact
// pixel hit — that pixel-dependence was the inconsistency. Both now land on the
// bare home grid at the top.
document.addEventListener("click", (e) => {
  if (!document.body.classList.contains("product-modal-open")) return;
  // When the cart is open, the logo's "go home" is owned by script-cart.js;
  // don't also close the modal here.
  if (window.HeaderState && window.HeaderState.state.cartOpen) return;
  const logo = e.target.closest && e.target.closest("#index-logo");
  if (!logo) return;
  e.preventDefault();
  e.stopPropagation();
  goHomeFromProductModal();
}, true);

// ─── Hover prefetch ─────────────────────────────────────────────────────────
// Start fetching the product page when the user mouses over a card, so the
// HTML is already in the cache by the time they click. Cuts perceived modal
// open time to near zero for hover-then-click flows.
function pmPrefetch(url) {
  if (PRODUCT_MODAL.cache[url]) return;
  // Store the in-flight Promise so a click during prefetch awaits it
  // rather than firing a duplicate request. On non-OK (403 rate limit,
  // 404, network error), pmFetchOk throws — delete the entry so the
  // next attempt actually retries instead of replaying the failure.
  PRODUCT_MODAL.cache[url] = pmFetchOk(pmModalUrl(url))
    .then((html) => {
      PRODUCT_MODAL.cache[url] = html;
      pmWarmProductScripts(html);
      return html;
    })
    .catch((err) => {
      delete PRODUCT_MODAL.cache[url];
      throw err;
    });
}

// Drop a <link rel="prefetch"> for each product-page script the first
// time we see them referenced. pmRunScripts later calls fetch() on the
// same URLs; with the HTTP cache already warm, those fetches resolve
// instantly. Tens-of-KB win on the first user-initiated PDP open.
const PRODUCT_SCRIPT_WARMED = Object.create(null);
function pmWarmProductScripts(html) {
  if (!html || typeof html !== "string") return;
  let doc;
  try { doc = new DOMParser().parseFromString(html, "text/html"); }
  catch (_) { return; }
  doc.querySelectorAll("script[src]").forEach((s) => {
    const src = s.getAttribute("src") || "";
    const isProductScript =
      src.indexOf("script-product-image-modal") !== -1 ||
      src.indexOf("script-product-variants") !== -1 ||
      src.indexOf("script-product-standard") !== -1;
    if (!isProductScript) return;
    const absolute = s.src; // resolved against the parsed doc's base
    if (PRODUCT_SCRIPT_WARMED[absolute]) return;
    PRODUCT_SCRIPT_WARMED[absolute] = true;
    const link = document.createElement("link");
    link.rel = "prefetch";
    link.as = "script";
    link.href = absolute;
    document.head.appendChild(link);
  });
}

// Hover prefetch was removed: even debounced, a cursor settling on cards while
// browsing the grid fired speculative ?view=modal requests that Shopify/
// Cloudflare flagged as bot traffic and 403'd. Prefetch now only fires on
// pointerdown (genuine press intent), and openProductModal fetches on demand
// if the cache is cold.

// Mobile has no hover, so the first tap pays the full fetch cost.
// pointerdown fires ahead of click on both touch and mouse, giving us
// an ~80–150ms head start before the click handler opens the modal.
document.addEventListener("pointerdown", (e) => {
  const a = e.target?.closest?.(".product-grid-image-container a");
  if (!a) return;
  const url = a.dataset.productUrl || a.getAttribute("href");
  if (url && url.startsWith("/products/")) pmPrefetch(url);
}, true);

// ─── Global listeners ───────────────────────────────────────────────────────
document.addEventListener("keydown", (e) => {
  if (e.key !== "Escape") return;
  // If the cart is open over the modal, Escape closes ONLY the cart — let
  // script-cart.js's Escape handler take it, and don't close the modal too.
  if (window.HeaderState && window.HeaderState.state.cartOpen) return;
  const overlay = document.getElementById(PRODUCT_MODAL.overlayId);
  if (overlay && overlay.style.display !== "none") closeProductModal(true);
});

window.addEventListener("popstate", (e) => {
  const overlay = document.getElementById(PRODUCT_MODAL.overlayId);
  const isOpen = overlay && overlay.style.display !== "none";
  const st = e.state;
  // Navigated (forward OR back) ONTO a productModal entry: re-open the PDP if it
  // isn't already showing. This is the forward-button case — without it the URL
  // changes to the product page but the modal never re-appears. Skip the pushState
  // (fromPopstate) since this history entry already exists.
  if (st && st.productModal && st.url) {
    if (!isOpen) openProductModal(st.url, { fromPopstate: true });
    return;
  }
  // Stepped off the product entry onto anything else → close the PDP if it's up.
  if (isOpen) closeProductModal(false);
});

document.addEventListener("DOMContentLoaded", function () {
  if (!document.body.classList.contains("template-index")) return;

  let isAnimating = false;
  let modalGeneration = 0;
  const STEP = 5;

  const fixedHeader = document.querySelector("header");
  const mainContent = document.getElementById("MainContent");

  const productGrid = document.getElementById("product-grid-main");

  // --- Fade in all home products once their images are ready ---
  // All products are already in the DOM via Liquid server-side rendering.
  (function fadeInHome() {
    const allHome = Array.from(
      document.querySelectorAll("#product-grid-main .product-grid-image-container")
    ).filter((c) => c.dataset.inHome === "true");

    const homeImages = allHome
      .map((c) => c.querySelector(".product-grid-image"))
      .filter(Boolean);

    function doFade() {
      requestAnimationFrame(() => {
        allHome.forEach((c) => {
          c.style.transition = "opacity 0.9s ease-in-out";
          c.style.opacity = "1";
        });
        setTimeout(() => {
          allHome.forEach((c) => {
            c.style.transition = "";
            c.style.opacity = "";
          });
        }, 950);
      });
    }

    if (homeImages.length === 0) { doFade(); return; }

    let loadedCount = 0;
    homeImages.forEach((img) => {
      if (img.complete && img.naturalWidth > 0) {
        loadedCount++;
        if (loadedCount === homeImages.length) doFade();
      } else {
        img.addEventListener("load", () => {
          loadedCount++;
          if (loadedCount === homeImages.length) doFade();
        });
        img.addEventListener("error", () => {
          loadedCount++;
          if (loadedCount === homeImages.length) doFade();
        });
      }
    });
  })();

  // --- Footer logo → soft scroll-to-top (home grid only) ---
  // On the home page the footer logo used to hard-link to "/", reloading the
  // page just to land back at the top. Instead, fade the home grid out, jump to
  // the top, and fade it back in — the scrollTo(0,0) re-triggers the scroll
  // sentinel, which restores the centre logo and hides the filter bar. Only the
  // home grid view qualifies: when a collection / VIEW ALL modal is open the
  // same footer node lives inside it, and there the logo keeps its default link.
  function goHomeTopFade() {
    const homeContainers = Array.from(
      document.querySelectorAll("#product-grid-main .product-grid-image-container")
    ).filter((c) => c.dataset.inHome === "true");

    homeContainers.forEach((c) => {
      c.style.transition = "opacity 0.3s ease-out";
      c.style.opacity = "0";
    });

    setTimeout(() => {
      window.scrollTo(0, 0);
      // Fade the centre logo back in on the SAME 0.9s curve as the grid (instead
      // of the scroll sentinel's default 0.3s showLogo).
      if (typeof window.__revealLogoTimed === "function") window.__revealLogoTimed(900);
      requestAnimationFrame(() => {
        homeContainers.forEach((c) => {
          c.style.transition = "opacity 0.9s ease-in-out";
          c.style.opacity = "1";
        });
        setTimeout(() => {
          homeContainers.forEach((c) => { c.style.transition = ""; c.style.opacity = ""; });
        }, 950);
      });
    }, 300);
  }

  document.addEventListener("click", function (e) {
    const link = e.target.closest && e.target.closest(".footer-logo-link");
    if (!link) return;
    // Home grid must be the active view — bail if a collection/VIEW ALL or PDP
    // modal is open (the footer node is inside the modal in that state).
    if (modal && modal.style.display === "block") return;
    const pdp = document.getElementById(PRODUCT_MODAL.overlayId);
    if (pdp && pdp.style.display !== "none" && pdp.style.display !== "") return;
    e.preventDefault();
    goHomeTopFade();
  });

  // --- Check if a container belongs to a tertiary collection ---
  function hasHandle(container, handle) {
    var handles = (container.dataset.tertiaryHandles || "").split(",").map(function (h) { return h.trim(); });
    return handles.indexOf(handle) !== -1;
  }

  // --- Header helpers ---
  function forceHeaderVisible() {
    if (!fixedHeader) return;
    fixedHeader.style.transition = "opacity 0.3s ease-out";
    fixedHeader.style.opacity = "1";
    fixedHeader.style.pointerEvents = "auto";
    setTimeout(() => { fixedHeader.style.transition = ""; }, 300);
  }

  function restoreHeaderOpacity() {
    if (!fixedHeader) return;
    const contentSection = document.querySelector("#shopify-section-index-content");
    const spacerOffset = contentSection ? contentSection.offsetHeight : (window.innerHeight - 70);
    const progress = spacerOffset > 0 ? Math.min(window.scrollY / spacerOffset, 1) : 1;
    fixedHeader.style.opacity = progress;
    fixedHeader.style.pointerEvents = progress > 0.9 ? "auto" : "none";
  }

  // showModalTitle / hideModalTitle now live in script-functions.js (shared).

  // --- Modal setup ---
  // Use existing element if script-index-filters.js already created it,
  // otherwise create it here. Both scripts must reference the same DOM node.
  let modal = document.getElementById("collection-modal");
  if (!modal) {
    modal = document.createElement("div");
    modal.id = "collection-modal";
    // In-flow body swap (not a fixed overlay): the modal participates in normal
    // document flow so the DOCUMENT scrolls over it, not an inner container. The
    // index sections are display:none'd while it's open (see hideSections), so
    // this becomes the body's content. No position/height/overflow — the page
    // owns scrolling. No padding-top: the modal sits right after the ~50px
    // #index-logo flow slot (kept via visibility:hidden), exactly like the index
    // grid does, so the collection lands in the same position as the home grid.
    modal.style.cssText =
      "display:none;width:100%;z-index:var(--z-collection-modal);background:transparent;opacity:1;box-sizing:border-box;";
    const modalInnerEl = document.createElement("div");
    modalInnerEl.id = "collection-modal-inner";
    modal.appendChild(modalInnerEl);
    document.body.appendChild(modal);
  }
  const modalInner = document.getElementById("collection-modal-inner");
  modalInner.style.display = "flex";
  modalInner.style.flexDirection = "column";
  // No min-height: the inner container sizes to its content exactly like the home
  // grid. A viewport-height floor here left a trailing blank block below the last
  // row on scrollable collections. Short collections still bottom-align the footer
  // because script-footer-visibility.js pins it (position:fixed) on its own.
  modalInner.style.minHeight = "";

  function appendModalFooter() {
    // No clone: MOVE the one real footer into the modal (last child, after the
    // grid). script-footer-visibility.js owns it and pins it (.footer-pinned)
    // only when the collection is shorter than the viewport — a tall collection
    // keeps the footer in-flow after the grid, exactly like the home page (the
    // old force-pin sat flush at the viewport bottom regardless of height).
    if (typeof window.__footerToModal === "function") window.__footerToModal(modalInner);
  }

  // --- Section visibility ---
  // display:none (not visibility:hidden) so the index sections fully leave the
  // document flow — the collection modal then becomes the body's content and the
  // document height/scrollbar reflect the collection, not the index grid behind it.
  function hideSections() {
    const root = mainContent || document;
    root.querySelectorAll("[id^='shopify-section-']").forEach((s) => {
      if (s.id === 'shopify-section-index-filters') return;
      // Banner persists across the collection modal until dismissed.
      if (s.id === 'shopify-section-index-message') return;
      s.style.display = "none";
    });
    document.querySelectorAll(".product-grid").forEach((g) => {
      if (!modal.contains(g)) g.style.display = "none";
    });
  }

  function showSections() {
    // Restore the index sections to flow FIRST, THEN re-home the footer. Order
    // matters: __footerToHome() re-measures the footer (script-footer-visibility
    // update()) to decide whether to pin it. If we re-home before the sections
    // are visible, it measures ~0 content above the footer and wrongly pins it
    // over the (about-to-reappear) tall home grid, where it hovers until the
    // next scroll forces a re-measure (bug P1). Restoring display first means
    // the re-measure sees the real home height. Safe vs. the old comment's
    // concern — restoring display does NOT clear modalInner.innerHTML, so the
    // one real footer node is still intact in the modal when we move it home.
    const root = mainContent || document;
    root.querySelectorAll("[id^='shopify-section-']").forEach((s) => {
      s.style.display = "";
    });
    document.querySelectorAll(".product-grid").forEach((g) => {
      if (!modal.contains(g)) g.style.display = "";
    });
    if (typeof window.__footerToHome === "function") window.__footerToHome();
  }

  // buildModalGrid now lives in script-functions.js (shared); call it with this
  // file's modalInner + appendModalFooter.

  // --- Animated open ---
  function animateAndOpen(tertiaryHandle, tertiaryTitle) {
    isAnimating = true;
    const myGeneration = ++modalGeneration;

    // Capture the home grid scroll before we swap the body out, so closing
    // restores the exact position. Only when opening fresh (not already in a
    // modal) — a filter change / rebuild while open must not overwrite it.
    if (modal.style.display !== "block") window.__indexHomeScrollY = window.scrollY;

    const homeContainers = Array.from(
      document.querySelectorAll(".product-grid-image-container")
    ).filter((c) => c.dataset.inHome === "true");

    // Fade out home grid
    homeContainers.forEach((c) => {
      c.style.transition = "opacity 0.10s ease-out";
      c.style.opacity = "0";
    });

    setTimeout(() => {
      if (myGeneration !== modalGeneration) return;

      // Restore home containers (hidden by hideSections below)
      homeContainers.forEach((c) => {
        c.style.transition = "none";
        c.style.opacity = "";
      });
      isAnimating = false;

      // ensureSecondaryState() (force body to the secondary scroll position) is
      // no longer called: with the index sections display:none'd the body swap
      // makes buildModalGrid's scrollTo(0,0) the single source of scroll truth.
      hideSections();
      forceHeaderVisible();
      showModalTitle(tertiaryTitle);

      const params = new URLSearchParams(window.location.search);
      params.set("collection", tertiaryHandle);
      history.pushState(
        { collectionModal: true, handle: tertiaryHandle, title: tertiaryTitle },
        "",
        window.location.pathname + "?" + params.toString()
      );

      modal.style.transition = "none";
      modal.style.opacity = "1";
      modal.style.display = "block";

      // Get all products that belong to this tertiary collection
      const sourceContainers = Array.from(
        document.querySelectorAll("#product-grid-main .product-grid-image-container")
      ).filter((c) => hasHandle(c, tertiaryHandle));

      const modalContainers = buildModalGrid(sourceContainers, modalInner, appendModalFooter);

      requestAnimationFrame(() => {
        modalContainers.forEach((c) => {
          c.style.transition = "opacity 0.9s ease-in-out";
          c.style.opacity = "1";
        });
      });
    }, 100);
  }

  // --- Simple open (popstate / auto-open) ---
  function openModal(tertiaryHandle, tertiaryTitle, { instant = false, pushHistory = true } = {}) {
    // Capture home scroll before the swap (only on a fresh open). buildModalGrid
    // then resets to top; closeModal restores this value.
    if (modal.style.display !== "block") window.__indexHomeScrollY = window.scrollY;
    hideSections();

    const earlyStyle = document.getElementById("cl-deeplink-hide");
    if (earlyStyle) earlyStyle.remove();

    const sourceContainers = Array.from(
      document.querySelectorAll("#product-grid-main .product-grid-image-container")
    ).filter((c) => hasHandle(c, tertiaryHandle));

    buildModalGrid(sourceContainers, modalInner, appendModalFooter);
    forceHeaderVisible();
    showModalTitle(tertiaryTitle || tertiaryHandle);

    modal.style.background = "transparent";

    if (instant) {
      modal.style.transition = "none";
      modal.style.opacity = "1";
      modal.style.display = "block";

      const modalContainers = Array.from(
        modalInner.querySelectorAll(".product-grid-image-container")
      );
      modalContainers.forEach((c) => { c.style.opacity = "1"; });
    } else {
      modal.style.opacity = "0";
      modal.style.display = "block";
      void modal.offsetHeight;
      modal.style.transition = "opacity 0.3s ease-out";
      modal.style.opacity = "1";
      setTimeout(() => { modal.style.transition = ""; }, 300);

      const modalContainers = Array.from(
        modalInner.querySelectorAll(".product-grid-image-container")
      );
      requestAnimationFrame(() => {
        modalContainers.forEach((c) => {
          c.style.transition = "opacity 0.9s ease-in-out";
          c.style.opacity = "1";
        });
      });
    }

    if (pushHistory) {
      const params = new URLSearchParams(window.location.search);
      params.set("collection", tertiaryHandle);
      history.pushState(
        { collectionModal: true, handle: tertiaryHandle, title: tertiaryTitle },
        "",
        window.location.pathname + "?" + params.toString()
      );
    }
  }

  function closeModal(updateHistory = true) {
    if (isAnimating) return;
    isAnimating = true;
    ++modalGeneration;

    window.HeaderState.exitModal();

    if (updateHistory) {
      const params = new URLSearchParams(window.location.search);
      params.delete("collection");
      const newUrl = window.location.pathname + (params.toString() ? "?" + params.toString() : "");
      history.pushState({}, "", newUrl);
    }

    const homeContainers = Array.from(
      document.querySelectorAll("#product-grid-main .product-grid-image-container")
    ).filter((c) => c.dataset.inHome === "true");

    // Instant reset for popstate navigation — let the browser own the visual transition.
    if (!updateHistory) {
      modal.style.transition = "none";
      modal.style.opacity = "0";
      modal.style.display = "none";
      hideModalTitle();
      const viewAllCloseBtn = document.getElementById("viewall-close-btn");
      if (viewAllCloseBtn) viewAllCloseBtn.style.display = "none";
      // Restore the index sections to flow, THEN restore the saved home scroll,
      // THEN recompute header opacity (which reads scrollY + section offsetHeight).
      showSections();
      window.scrollTo(0, window.__indexHomeScrollY || 0);
      restoreHeaderOpacity();
      homeContainers.forEach((c) => {
        c.style.transition = "none";
        c.style.opacity = "";
      });
      modalInner.innerHTML = "";
      isAnimating = false;
      return;
    }

    // Animated close for user-triggered dismissals.
    const modalContainers = Array.from(
      modalInner.querySelectorAll(".product-grid-image-container")
    );
    modalContainers.forEach((c) => {
      c.style.transition = "opacity 0.10s ease-out";
      c.style.opacity = "0";
    });

    homeContainers.forEach((c) => {
      c.style.transition = "none";
      c.style.opacity = "0";
    });

    setTimeout(() => {
      modal.style.transition = "none";
      modal.style.display = "none";
      hideModalTitle();

      const viewAllCloseBtn = document.getElementById("viewall-close-btn");
      if (viewAllCloseBtn) viewAllCloseBtn.style.display = "none";

      // Restore sections to flow and the saved home scroll before recomputing
      // header opacity (it reads scrollY + section offsetHeight).
      showSections();
      window.scrollTo(0, window.__indexHomeScrollY || 0);

      requestAnimationFrame(restoreHeaderOpacity);

      requestAnimationFrame(() => {
        homeContainers.forEach((c) => {
          c.style.transition = "opacity 0.9s ease-in-out";
          c.style.opacity = "1";
        });
      });

      isAnimating = false;
      const closeGeneration = modalGeneration;

      setTimeout(() => {
        if (modalGeneration !== closeGeneration) return;
        if (modal.style.display === "block") return;
        homeContainers.forEach((c) => {
          c.style.transition = "";
          c.style.opacity = "";
        });
        modalInner.innerHTML = "";
      }, 950);
    }, 100);
  }

  // --- Click handler ---
  document.addEventListener("click", function (e) {
    const link = e.target.closest(".product-grid-image-container a");
    if (!link) return;

    // Modal is open: any product click opens the product iframe modal (PDP)
    if (modal.style.display === "block") {
      const productUrl = link.dataset.productUrl || link.getAttribute("href");
      if (productUrl) {
        e.preventDefault();
        openProductModal(productUrl);
      }
      return;
    }

    const tertiaryHandles = link.dataset.tertiaryHandles || "";
    const tertiaryTitles = link.dataset.tertiaryTitles || "";

    // No tertiary collection (only home+all): open product iframe modal
    // instead of letting the browser navigate to the PDP.
    if (!tertiaryHandles) {
      const productUrl = link.dataset.productUrl || link.getAttribute("href");
      if (productUrl) {
        e.preventDefault();
        openProductModal(productUrl);
      }
      return;
    }
    if (isAnimating) return;

    e.preventDefault();

    // Resolve the name filter match up front (needed by doOpen below)
    const productHandles = tertiaryHandles.split(",").map((h) => h.trim());
    const nameMenuItems = document.querySelectorAll("[data-menu-type='name']");
    let matchedNameItem = null;

    for (const item of nameMenuItems) {
      const url = item.getAttribute("data-menu-url") || "";
      const allMatch = url.match(/\/all\/([^/?]+)/);
      const colMatch = url.match(/\/collections\/([^/?]+)/);
      const handle = allMatch ? allMatch[1].toLowerCase() : (colMatch ? colMatch[1].toLowerCase() : null);
      if (handle && productHandles.indexOf(handle) !== -1) {
        matchedNameItem = item;
        break;
      }
    }

    // Opens the collection modal (runs after any logo transition completes)
    function doOpen() {
      if (matchedNameItem) {
        const nameDropdown = document.querySelector("[data-filter='name']");
        if (nameDropdown) {
          nameDropdown.value = matchedNameItem.getAttribute("data-menu-item");
          nameDropdown.dispatchEvent(new Event("change"));
          // Override title with the actual collection title from the product
          const firstTitle = tertiaryTitles.split(",")[0].trim() || tertiaryHandles.split(",")[0].trim();
          const modalTitleEl = document.getElementById("collection-modal-title");
          if (modalTitleEl) modalTitleEl.textContent = firstTitle;
          return;
        }
      }
      const firstHandle = tertiaryHandles.split(",")[0].trim();
      const firstTitle = tertiaryTitles.split(",")[0].trim() || firstHandle;
      const label = resolveCollectionLabel(firstHandle);
      window.HeaderState.setNameDisplay(label);
      animateAndOpen(firstHandle, label);
    }

    // If the logo is currently showing, run the logo→filters transition first,
    // then open the modal once the menu is visible.
    window.HeaderState.enterModal({ fromProductClick: true, callback: doOpen });
  });

  // --- Close on any click that isn't on a link ---
  // DISABLED (for now): clicking empty negative space on the GRID/body no longer
  // closes the open modal. Re-enable by uncommenting.
  // modal.addEventListener("click", function (e) {
  //   if (e.target.closest(".footer-wrapper")) return;
  //   if (!e.target.closest("a") && !isAnimating) closeModal();
  // });

  // --- Close modal when clicking non-interactive areas of the header or menu ---
  // KEPT: negative-space clicks on the nav BAR (header/menu) still close — only
  // the grid handler above was disabled.
  [document.getElementById("menu"), fixedHeader].forEach((el) => {
    if (!el) return;
    el.addEventListener("click", function (e) {
      if (modal.style.display !== "block") return;
      if (!e.target.closest("a, button, [onclick]") && !isAnimating) closeModal();
    });
  });

  // --- Close on Escape ---
  document.addEventListener("keydown", function (e) {
    if (e.key !== "Escape" || modal.style.display !== "block" || isAnimating) return;
    // If the PDP modal is open ON TOP of this collection modal, Escape belongs to
    // the PDP — its own handler history.back()s, which restores THIS collection
    // underneath. Without this bail both Escape handlers fire on one keypress:
    // the collection is still display:block (only faded to opacity 0 beneath the
    // PDP), so closeModal() here tears it down too and Escape skips straight to
    // the home grid instead of returning to the previous (collection) grid. The
    // PDP overlay's display stays "block" until its 100ms teardown timer, so it
    // reads as open throughout this synchronous keydown regardless of handler order.
    var pdpOverlay = document.getElementById(PRODUCT_MODAL.overlayId);
    if (pdpOverlay && pdpOverlay.style.display !== "none") return;
    closeModal();
  });

  // --- Handle browser back/forward ---
  window.addEventListener("popstate", function (e) {
    // Filter / VIEW ALL state changes are handled by script-index-filters.js
    if (e.state && (e.state.filterModal || e.state.viewAll)) return;

    isAnimating = false;
    ++modalGeneration;

    if (e.state && e.state.collectionModal) {
      // If the collection-modal is already mounted (the common case: user
      // opened it, opened a PDP from it, and is now back-buttoning), skip
      // the rebuild. pmFadeOutCurrentView only set its opacity to 0 when
      // the PDP opened — closeProductModal's pmFadeInView is already
      // restoring opacity from this same popstate. Re-running openModal
      // here calls buildModalGrid which wipes modalInner and re-clones
      // products, resetting scroll to 0 — visible as the grid jumping
      // upward as it fades back in.
      if (modal.style.display === "block") return;
      hideSections();
      openModal(e.state.handle, e.state.title || e.state.handle, { instant: true, pushHistory: false });
    } else if (modal.style.display === "block") {
      closeModal(false);
    }
  });

  // --- Resolve a display label for a collection handle ---
  // Priority: NAME menu link title → collection backup_title metafield →
  // collection title (via data-tertiary-titles on a matching product) → raw handle.
  function resolveCollectionLabel(handle) {
    const lower = handle.toLowerCase();

    const menuItems = document.querySelectorAll("[data-menu-type='name']");
    for (const item of menuItems) {
      const url = item.getAttribute("data-menu-url") || "";
      const m = url.match(/\/all\/([^/?]+)/) || url.match(/\/collections\/([^/?]+)/);
      if (m && m[1].toLowerCase() === lower) {
        return item.getAttribute("data-menu-item");
      }
    }

    const meta = document.querySelector("#collection-meta [data-col-handle='" + handle + "']");
    if (meta) {
      const backup = meta.getAttribute("data-col-backup-title");
      if (backup) return backup;
    }

    const firstMatch = document.querySelector(
      "#product-grid-main .product-grid-image-container[data-tertiary-handles*='" + handle + "']"
    );
    if (firstMatch) {
      const handles = (firstMatch.dataset.tertiaryHandles || "").split(",").map((h) => h.trim());
      const titles = (firstMatch.dataset.tertiaryTitles || "").split(",").map((t) => t.trim());
      const idx = handles.indexOf(handle);
      if (idx !== -1 && titles[idx]) return titles[idx];
    }

    return handle;
  }

  // --- Auto-open from URL ---
  const initialParams = new URLSearchParams(window.location.search);
  const initialHandle = initialParams.get("collection");
  const hasFilterParam = initialParams.get("name") || initialParams.get("category") || initialParams.get("finish");
  // Only handle ?collection= here; filter params are handled by script-index-filters.js
  if (initialHandle && !hasFilterParam) {
    const label = resolveCollectionLabel(initialHandle);
    // PRESERVE the deep-link URL (keep ?collection=h in the address bar) so the
    // view is reloadable and shareable. We do NOT clean it to "/". Instead we
    // ATTACH the matching collectionModal state to this initial history entry
    // (a fresh page load lands here with null state). That state is what the
    // popstate handler reads to RE-open the collection if the user opens a PDP
    // from it and then steps back onto this entry — without it, back would close
    // the modal but leave ?collection=h stale in the bar. pushHistory:false on
    // openModal below avoids stacking a duplicate entry on top of this one.
    history.replaceState(
      { collectionModal: true, handle: initialHandle, title: label },
      "",
      window.location.pathname + window.location.search
    );
    // Set the header to its collection state FIRST (hide the logo, show the
    // filter bar) — the deep-link path skips the product-click
    // enterModal→revealFilters that normally does this, which is why the logo
    // flashed before the name. Then build the modal, name it, and only then drop
    // the early-hide style so nothing in between is visible.
    if (window.HeaderState.revealFilters) window.HeaderState.revealFilters();
    // instant:false → the collection grid FADES in on a deep-link reload, exactly
    // like the home grid's fadeInHome. (popstate restores from a PDP still use
    // instant:true so stepping back doesn't re-animate.)
    openModal(initialHandle, label, { instant: false, pushHistory: false });
    window.HeaderState.setNameDisplay(label);
    const earlyStyle = document.getElementById("cl-deeplink-hide");
    if (earlyStyle) earlyStyle.remove();
  }

  window.closeCollectionModal = closeModal;
});
